import * as THREE from "three";
import Renderer from "../../renderer/renderer";
import CameraService from "../camera/camera.service";
import ArtworkLoader from "../../loaders/artwork/artwork.loader";
import SoundService from "../sound/sound.service";
import { Utils } from "../../utils/utils";
import AppData from "../../app.data";
import { EventInputHandler } from "./handlers/input.handler";
import { EventRaycaster } from "./handlers/raycast.handler";
import { EventArtworkManipulator } from "./handlers/artwork.handler";
import { EventMediaHandler } from "./handlers/media.handler";

const appData = new AppData();

/**
 * Manages all user interactions and events in the 3D environment
 * Handles mouse, touch, keyboard inputs, and artwork/space interactions
 * Controls artwork selection, movement, and space editing functionality
 */
class EventService {
  /** Camera service instance */
  cameraService: CameraService;
  /** Renderer service instance */
  renderService: Renderer;
  /** Canvas element reference */
  canvas: HTMLCanvasElement;
  /** Artwork loader service instance */
  artworkLoaderService: ArtworkLoader;
  /** Sound service instance */
  private soundService: SoundService;
  /** Utility functions */
  utils: Utils;

  // Handlers
  public inputHandler: EventInputHandler;
  public raycasterHandler: EventRaycaster;
  public artworkHandler: EventArtworkManipulator;
  public mediaHandler: EventMediaHandler;

  /** Callback for diorama click events */
  public onUserDioramaClicked: Function;
  /** Callback for artwork click events */
  public onArtworkClicked: Function;
  /** Callback for space edit events */
  public onSpaceEditClicked: Function;

  /**
   * Initializes the event service with required dependencies
   * @param RenderService Renderer instance
   * @param canvas Canvas element for event handling
   * @param cameraService Camera service instance
   * @param artworkLoaderService Artwork loader instance
   * @param soundService Sound service instance
   */
  constructor(RenderService: Renderer, canvas: HTMLCanvasElement, cameraService: CameraService, artworkLoaderService: ArtworkLoader, soundService: SoundService) {
    this.cameraService = cameraService;
    this.renderService = RenderService;
    this.canvas = canvas;
    this.artworkLoaderService = artworkLoaderService;
    this.soundService = soundService;
    this.utils = new Utils();

    this.artworkHandler = new EventArtworkManipulator(this.renderService);
    this.mediaHandler = new EventMediaHandler(this.soundService);
    this.raycasterHandler = new EventRaycaster(
        this,
        this.renderService,
        this.canvas,
        this.artworkLoaderService,
        this.artworkHandler,
        this.mediaHandler
    );
    this.inputHandler = new EventInputHandler(
        this.cameraService,
        this.renderService,
        this.raycasterHandler,
        this.mediaHandler,
        this.artworkHandler,
        this.canvas
    );
  }

  // --- Delegation Methods ---

  /**
   * Enables artwork selection and movement mode
   */
  public selectAndMoveArtwork = () => {
    this.artworkHandler.selectAndMoveArtwork();
  }

  /**
   * Gets the currently selected artwork
   */
  public getSelectedArtwork() {
    return this.artworkHandler.getSelectedArtwork();
  }

  public resetSlotTransforms = (slot: string) => {
    this.artworkHandler.resetSlotTransforms(slot);
  }

  /**
   * Finalizes artwork movement and returns updated positions
   */
  public getMovedArtworks() {
    return this.artworkHandler.getMovedArtworks();
  }

  public setKeyUpAvailability(availability: boolean) {
    this.inputHandler.setKeyUpAvailability(availability);
  }

  public setViewerRotationEnabled(enabled: boolean) {
    this.inputHandler.setViewerRotationEnabled(enabled);
  }

  public setDioramaClickedMethod(method: Function) {
    this.onUserDioramaClicked = method;
  }

  public setArtworkClickedMethod(method: Function) {
    this.onArtworkClicked = method;
  }

  public setSpaceEditClickEvent(method: any) {
    this.onSpaceEditClicked = method;
  }

  // --- Event Listeners Delegation ---

  public slotClickHandler = (event) => {
    this.raycasterHandler.slotClickHandler(event);
  }

  public mouseOverArtworksHandler = (event: MouseEvent) => {
    this.raycasterHandler.mouseOverArtworksHandler(event);
  }

  public keyDownEvent = (event) => {
    this.inputHandler.keyDownEvent(event);
  }

  public keyUpEvent = (event) => {
    this.inputHandler.keyUpEvent(event);
  }

  public joystickTouchStart = (event: TouchEvent) => {
    this.inputHandler.joystickTouchStart(event);
  }

  public joystickTouchMove = (event) => {
    this.inputHandler.joystickTouchMove(event);
  }

  public joystickTouchEnd = (event) => {
    this.inputHandler.joystickTouchEnd(event);
  }

  public touchStart = (event: TouchEvent) => {
    this.inputHandler.touchStart(event);
  }

  public touchMove = (event: TouchEvent) => {
    this.inputHandler.touchMove(event);
  }

  public touchEnd = (event) => {
    this.inputHandler.touchEnd(event);
  }

  public mouseDown = (event) => {
    if (event.which === 1) {
      this.inputHandler.mouseDown(event);
    }
  }

  public mouseMove = (event) => {
    event.preventDefault();
    if (event.which === 1) {
      this.inputHandler.mouseMove(event);
    } else {
       this.mouseOverArtworksHandler(event);
    }
  }

  public passiveMouseMove = (event) => {
    this.inputHandler.passiveMouseMove(event);
  }

  public mouseUp = (event) => {
    this.inputHandler.mouseUp(event);
  }

  public mouseLeave = (event) => {
    this.inputHandler.mouseLeave(event);
  }
  
  // --- Resize Logic (Delegated) ---

  public onWindowResize = () => {
    this.inputHandler.onWindowResize();
  }

}

export default EventService;
